import { graphql } from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { useFragment } from "react-relay";
import {
  CDPType,
  useBrandAudiences_brand$data,
  useBrandAudiences_brand$key,
} from "./__generated__/useBrandAudiences_brand.graphql";

export type { CDPType };

const CDP_TYPE_ORDER = ["KINN", "KLAVIYO", "SHOPIFY", "OMETRIA", "EMARSYS"];

const brandFragment = graphql`
  fragment useBrandAudiences_brand on BrandType {
    cdps {
      cdpType
      audiences {
        id
        name
        type
      }
    }
  }
`;

export enum KinnAudienceType {
  LOGGED_IN = "Logged In",
  LOGGED_OUT = "Logged Out",
}

export const KINN_CDP_TYPE = "KINN";

export type CDP = useBrandAudiences_brand$data["cdps"][number];

export type CDPAudience = CDP["audiences"][number] & {
  providerType: CDPType;
};

const useBrandAudiences = ({
  brand: brandKey,
}: {
  brand: useBrandAudiences_brand$key;
  nullIfLocalizationDisabled?: boolean;
}) => {
  const brand = useFragment(brandFragment, brandKey);
  const cdps = useMemo(
    () => brand.cdps.filter((cdp) => cdp.audiences.length > 0),
    [brand]
  );

  const cdpAudiences = useMemo(() => {
    const audiences: CDPAudience[][] = cdps.map((cdp) => {
      return cdp.audiences.map((audience) => ({
        ...audience,
        providerType: cdp.cdpType,
      }));
    });
    return audiences.flat().sort((a, b) => -b.type.localeCompare(a.type));
  }, [cdps]);

  const cdpTypes = useMemo(
    () =>
      cdps
        .map((cdp) => cdp.cdpType)
        .sort((a, b) => CDP_TYPE_ORDER.indexOf(a) - CDP_TYPE_ORDER.indexOf(b)),
    [cdps]
  );

  return {
    cdpAudiences,
    cdpTypes,
  };
};

export default useBrandAudiences;
